import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useUser } from '../contexts/UserContext';
import textToSpeech from '../utils/textToSpeech';
import { logger } from '../utils/logger';
import AuthForm from './AuthForm';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { progressSync } from '../utils/progressSync';
import { useGameProgress } from '../hooks/useGameProgress';
import { avatars as defaultAvatars } from '../data/appConfigData';

const ProfileManager = memo(({ onClose = null, progress: progressProp = null, saveProgress: saveProgressProp = null, avatars: avatarsProp = null } = {}) => {
  const { user, userProfile, updateUserProfile, signInWithGoogle, signInWithApple, logout } = useUser();
  // Use props if provided (from App), otherwise use hook (standalone)
  const progressHook = useGameProgress();
  const progress = progressProp || progressHook.progress;
  const saveProgress = saveProgressProp || progressHook.saveProgress;
  const avatars = avatarsProp || defaultAvatars;
  // Track active tab - use sessionStorage to persist across remounts
  const getInitialTab = () => {
    try {
      const saved = sessionStorage.getItem('profileManager_activeTab');
      return saved || 'avatar';
    } catch {
      return 'avatar';
    }
  };
  const [activeTab, setActiveTab] = useState(() => getInitialTab());
  
  // Persist tab to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('profileManager_activeTab', activeTab);
      logger.debug('[ProfileManager] Tab saved to sessionStorage:', activeTab);
    } catch (error) {
      logger.warn('[ProfileManager] Failed to save tab to sessionStorage:', error);
    }
  }, [activeTab]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  // Initialize displayName from userProfile if available
  const getInitialDisplayName = () => {
    if (userProfile) {
      return userProfile.display_name || userProfile.displayName || userProfile.email?.split('@')[0] || '';
    }
    return '';
  };
  const [displayName, setDisplayName] = useState(getInitialDisplayName);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [localAvatar, setLocalAvatar] = useState(null); // Track avatar locally for immediate UI update
  
  // Check if user is a demo/test user
  const isDemoUser = user && (
    user.is_demo || 
    user.is_fallback || 
    user.is_developer ||
    (user.id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id))
  );
  
  // Check if user is logged in (not demo user)
  const isLoggedIn = user && !isDemoUser;

  // Get the current display name from userProfile
  const currentDisplayName = useMemo(() => {
    if (userProfile) {
      return userProfile.display_name || userProfile.displayName || userProfile.email?.split('@')[0] || '';
    }
    return '';
  }, [userProfile]);

  // Initialize display name from userProfile
  useEffect(() => {
    if (userProfile && currentDisplayName) {
      // Always update displayName when we have a name from userProfile and it's different
      setDisplayName(prev => {
        if (prev !== currentDisplayName) {
          logger.debug('[ProfileManager] Setting displayName from userProfile:', currentDisplayName, 'previous:', prev);
          return currentDisplayName;
        }
        return prev;
      });
    }
  }, [userProfile, currentDisplayName]); // Track userProfile and currentDisplayName changes
  
  // Separate effect to initialize localAvatar - single source of truth
  useEffect(() => {
    // Priority: progress.avatar (always most up-to-date) > userProfile.avatar > 'default'
    const currentAvatarId = progress?.avatar || userProfile?.avatar || 'default';
    
    logger.debug('[ProfileManager] Initializing localAvatar:', {
      progressAvatar: progress?.avatar,
      userProfileAvatar: userProfile?.avatar,
      selectedAvatar: currentAvatarId
    });
    
    setLocalAvatar(currentAvatarId);
  }, [progress?.avatar, userProfile?.avatar]); // Only update when these specific values change
  
  // Log when component re-renders to track if it's being remounted
  useEffect(() => {
    const savedTab = sessionStorage.getItem('profileManager_activeTab');
    logger.debug('[ProfileManager] Component rendered, activeTab:', activeTab, 'savedTab:', savedTab);
    // Only restore if there's a mismatch and we're not in the middle of a state update
    if (activeTab !== savedTab && savedTab && activeTab === 'avatar' && savedTab !== 'avatar') {
      logger.warn('[ProfileManager] WARNING: Tab was reset to avatar! Restoring from sessionStorage:', savedTab);
      // Use setTimeout to avoid state update during render
      setTimeout(() => {
        setActiveTab(savedTab);
      }, 0);
    }
  });
  
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out? Your progress will still be saved locally.')) {
      await logout();
      onClose(); // Close the profile manager
      // Don't reload - logout function already sets demo user
      // The UI will update automatically via React state
    }
  };
  const [availableVoices, setAvailableVoices] = useState([]);
  const [currentVoice, setCurrentVoice] = useState(null);
  const [isTestingVoice, setIsTestingVoice] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({
    rate: 0.95,
    pitch: 1.0,
    volume: 0.9
  });

  // Convert avatars object from appConfigData to array format for the grid
  const avatarsList = useMemo(() => {
    return Object.entries(avatars).map(([id, avatar]) => ({
      id,
      emoji: avatar.emoji,
      name: avatar.name
    }));
  }, [avatars]);

  const themes = useMemo(() => [
    { id: 'light', name: 'Light', color: 'bg-yellow-200', text: 'text-yellow-800' },
    { id: 'dark', name: 'Dark', color: 'bg-gray-800', text: 'text-white' },
    { id: 'ocean', name: 'Ocean', color: 'bg-blue-200', text: 'text-blue-800' },
    { id: 'forest', name: 'Forest', color: 'bg-green-200', text: 'text-green-800' },
    { id: 'sunset', name: 'Sunset', color: 'bg-orange-200', text: 'text-orange-800' }
  ], []);

  // Load voices and settings when component mounts
  useEffect(() => {
    const loadVoices = () => {
      const voices = textToSpeech.getEnglishVoices();
      setAvailableVoices(voices);
      const current = textToSpeech.getCurrentVoice();
      setCurrentVoice(current);
      
      // Load voice settings
      const preferences = textToSpeech.getVoicePreferences();
      if (preferences.userSettings) {
        setVoiceSettings({
          rate: preferences.userSettings.rate ?? 0.95,
          pitch: preferences.userSettings.pitch ?? 1.0,
          volume: preferences.userSettings.volume ?? 0.9
        });
      }
    };

    // Load voices immediately
    loadVoices();

    // Also listen for voice changes
    const checkVoices = setInterval(() => {
      const voices = textToSpeech.getEnglishVoices();
      if (voices.length > 0) {
        setAvailableVoices(voices);
        clearInterval(checkVoices);
      }
    }, 100);

    return () => clearInterval(checkVoices);
  }, []);

  // Voice settings handlers
  const handleVoiceSettingChange = (setting, value) => {
    const newSettings = {
      ...voiceSettings,
      [setting]: parseFloat(value)
    };
    setVoiceSettings(newSettings);
    
    // Save to textToSpeech (includes voice name if available)
    textToSpeech.saveUserPreferences({
      ...newSettings,
      voiceName: currentVoice?.name || null
    });
  };

  // Voice-related functions
  const handleVoiceChange = async (voiceName) => {
    setIsLoading(true);
    try {
      const success = textToSpeech.setVoice(voiceName);
      if (success) {
        const updatedVoice = textToSpeech.getCurrentVoice();
        setCurrentVoice(updatedVoice);
        
        // Save voice preference
        textToSpeech.saveUserPreferences({
          ...voiceSettings,
          voiceName: voiceName
        });
        await updateUserProfile({
          preferences: {
            ...userProfile.preferences,
            voice: voiceName
          }
        });
      }
    } catch (error) {
      logger.error('Error updating voice:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const testVoice = (voiceName) => {
    setIsTestingVoice(true);
    const sampleText = "Hello! This is how I sound. I'm your learning assistant!";
    textToSpeech.testVoice(voiceName, sampleText);
    
    // Reset testing state after a delay
    setTimeout(() => {
      setIsTestingVoice(false);
    }, 3000);
  };

  const handleAvatarChange = async (avatarId, event) => {
    if (isLoading) {
      logger.warn('[ProfileManager] Avatar change already in progress, ignoring');
      return; // Prevent double-clicks
    }
    
    logger.info('[ProfileManager] Starting avatar change to:', avatarId);
    setIsLoading(true);
    
    // IMMEDIATE UI UPDATE - set local avatar right away for instant feedback
    setLocalAvatar(avatarId);
    
    // Set a safety timeout to clear loading state if something goes wrong
    const safetyTimeout = setTimeout(() => {
      logger.warn('[ProfileManager] Avatar change timeout reached, clearing loading state');
      setIsLoading(false);
    }, 5000); // 5 second safety timeout
    
    try {
      // Update progress.avatar FIRST (works even if userProfile not loaded)
      // Create a completely new object to ensure reference changes
      const updatedProgress = {
        ...progress,
        avatar: avatarId,
        _lastUpdated: Date.now() // Force new reference for React to detect change
      };
      
      logger.debug('[ProfileManager] Calling saveProgress with:', {
        oldAvatar: progress.avatar,
        newAvatar: avatarId
      });
      
      // Call saveProgress - it updates state synchronously via setProgress
      // Await it to ensure state update completes
      await saveProgress(updatedProgress, true);
      
      logger.info('[ProfileManager] Progress avatar updated successfully to:', avatarId);
      logger.debug('[ProfileManager] Verifying progress state after save:', {
        updatedProgressAvatar: updatedProgress.avatar,
        currentProgressAvatar: progress.avatar
      });
      
      // Update userProfile in database (only if logged in) - asynchronous, non-blocking
      if (isLoggedIn && user) {
        // Don't await - let it happen in background to make UI feel faster
        updateUserProfile({ avatar: avatarId }).then(() => {
          logger.debug('[ProfileManager] UserProfile avatar updated in database');
        }).catch((profileError) => {
          logger.warn('[ProfileManager] Could not update userProfile:', profileError.message);
          // Don't fail - progress.avatar is already updated
        });
      }
      
      // Show brief success feedback
      if (event?.target) {
        const button = event.target.closest('button');
        if (button) {
          button.style.transform = 'scale(0.95)';
          setTimeout(() => {
            button.style.transform = '';
          }, 200);
        }
      }
    } catch (error) {
      logger.error('[ProfileManager] Error updating avatar:', error);
      // Revert local avatar on error
      const fallbackAvatar = userProfile?.avatar || progress?.avatar || 'default';
      setLocalAvatar(fallbackAvatar);
      logger.warn('[ProfileManager] Reverted avatar to:', fallbackAvatar);
      
      // Show user-friendly error message
      alert('Failed to update avatar. Please try again.');
    } finally {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);
      
      // Always clear loading state
      setIsLoading(false);
      logger.debug('[ProfileManager] Avatar change complete, loading state cleared');
    }
  };

  const handlePreferenceChange = async (key, value) => {
    setIsLoading(true);
    try {
      await updateUserProfile({
        preferences: {
          ...userProfile.preferences,
          [key]: value
        }
      });
    } catch (error) {
      logger.error('Error updating preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleThemeChange = async (themeId) => {
    setIsLoading(true);
    try {
      await updateUserProfile({
        preferences: {
          ...userProfile.preferences,
          theme: themeId
        }
      });
    } catch (error) {
      logger.error('Error updating theme:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle display name update
  const handleDisplayNameUpdate = async () => {
    if (!displayName.trim()) {
      alert('Please enter a name');
      return;
    }

    setIsLoading(true);
    try {
      await updateUserProfile({
        display_name: displayName.trim()
      });
      logger.info('Display name updated successfully');
    } catch (error) {
      logger.error('Error updating display name:', error);
      alert('Failed to update name. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset progress
  const handleResetProgress = async () => {
    if (!showResetConfirm) {
      setShowResetConfirm(true);
      return;
    }

    setIsResetting(true);
    try {
      // Reset local storage progress
      const defaultProgress = {
        completedSubjects: {},
        totalScore: 0,
        streak: 0,
        lastPlayed: null,
        achievements: [],
        unlockedThemes: ['default'],
        selectedTheme: 'default',
        avatar: userProfile?.avatar || 'default',
        certificates: [],
        questionHistory: {},
        stats: {
          totalQuestionsAnswered: 0,
          correctAnswers: 0,
          perfectScores: 0,
          timeSpent: 0,
          favoriteSubject: null
        }
      };

      // Save reset progress
      saveProgress(defaultProgress, true);

      // If logged in, also reset in database
      if (isLoggedIn && user) {
        try {
          await progressSync.syncProgress(defaultProgress, user, true);
          logger.info('Progress reset in database');
        } catch (error) {
          logger.warn('Failed to reset progress in database, but local reset succeeded:', error);
        }
      }

      // Clear analytics data
      storage.remove(STORAGE_KEYS.OFFLINE_QUEUE);
      
      // Clear cached content (optional - user might want to keep it)
      // await contentCache.clearAllCaches();

      setShowResetConfirm(false);
      alert('Progress has been reset! You can start fresh now. 🎉');
      
      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      logger.error('Error resetting progress:', error);
      alert('Failed to reset progress. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  // Don't return null - allow rendering even without userProfile (for guest users)
  // The component will handle the case where userProfile is null

  // Debug logging - IMMEDIATE on render
  useEffect(() => {
    logger.info('ProfileManager: Component rendered/mounted');
    logger.debug('ProfileManager: Props:', { onClose: typeof onClose, onCloseAvailable: !!onClose });
    logger.debug('ProfileManager: User state:', { 
      hasUser: !!user, 
      hasProfile: !!userProfile,
      isLoggedIn,
      isDemoUser,
      userId: user?.id,
      userEmail: user?.email
    });
    logger.debug('ProfileManager: Using React Portal to render at document.body');
    // Verify portal target exists
    if (typeof document !== 'undefined' && document.body) {
      logger.debug('ProfileManager: document.body exists, portal will work');
    } else {
      logger.warn('ProfileManager: document.body not available, will render inline');
    }
  }, []);

  // Log when onClose changes
  useEffect(() => {
    if (onClose) {
      logger.debug('ProfileManager: onClose function available');
    } else {
      logger.warn('ProfileManager: onClose function is null/undefined');
    }
  }, [onClose]);

  // Use React Portal to ensure modal renders at the top level
  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
      onClick={(e) => {
        // Close when clicking backdrop
        if (e.target === e.currentTarget && onClose) {
          logger.debug('ProfileManager: Backdrop clicked, closing modal');
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">My Profile 🎨</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ✕
            </button>
          </div>
          <p className="text-purple-100 mt-2">Customize your learning experience!</p>
          {isLoggedIn && userProfile && (
            <div className="mt-3 pt-3 border-t border-purple-300">
              <p className="text-sm text-purple-100">
                Logged in as: <span className="font-semibold">{userProfile.display_name || userProfile.email}</span>
              </p>
            </div>
          )}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Login Notice for Demo Users */}
          {isDemoUser && !showLogin && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-6 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-2xl">🔐</span>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">
                    You're using a demo account
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Log in with your real account to sync your progress across devices!
                  </p>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={() => setShowLogin(true)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                    >
                      Log In
                    </button>
                    <button
                      onClick={onClose}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors"
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show Auth Form if login requested */}
          {showLogin && (
            <div className="p-6">
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold">Log In to Sync Progress</h3>
                <button
                  onClick={() => setShowLogin(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <AuthForm 
                isSignUp={false}
                onSuccess={() => {
                  setShowLogin(false);
                  onClose();
                }}
              />
            </div>
          )}

        {/* Regular Profile Manager Content (hide if showing login) */}
        {!showLogin && (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'avatar', name: 'Avatar', emoji: '👤' },
                { id: 'preferences', name: 'Settings', emoji: '⚙️' },
                { id: 'theme', name: 'Theme', emoji: '🎨' },
                ...(isLoggedIn ? [{ id: 'account', name: 'Account', emoji: '🔐' }] : [])
              ].map(tab => (
            <button
              key={tab.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation?.();
                logger.debug('ProfileManager: Tab clicked', { newTab: tab.id, currentTab: activeTab });
                
                // Use a function updater to ensure we're setting the correct value
                setActiveTab((prevTab) => {
                  if (prevTab !== tab.id) {
                    logger.debug('[ProfileManager] Tab changing from', prevTab, 'to', tab.id);
                    return tab.id;
                  }
                  logger.warn('[ProfileManager] Tab already set to', tab.id, 'but click happened?');
                  return prevTab;
                });
                
                // Verify it was set correctly after a brief delay
                setTimeout(() => {
                  const current = sessionStorage.getItem('profileManager_activeTab');
                  if (current !== tab.id) {
                    logger.error('[ProfileManager] ERROR: Tab was not saved correctly! Expected:', tab.id, 'Got:', current);
                  }
                }, 100);
              }}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              type="button"
            >
              <span className="text-xl mr-2">{tab.emoji}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'avatar' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Choose Your Avatar! 🎭</h3>
              <div className="grid grid-cols-4 gap-4">
                {avatarsList.map(avatar => (
                  <button
                    key={avatar.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAvatarChange(avatar.id, e);
                    }}
                    disabled={isLoading}
                  className={`p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                    localAvatar === avatar.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-4xl mb-2">{avatar.emoji}</div>
                    <div className="text-sm font-medium">{avatar.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold mb-4">Learning Preferences 📚</h3>
              
              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Level 🎯
                </label>
                <div className="flex space-x-4">
                  {[
                    { value: 'easy', label: 'Easy', emoji: '😊' },
                    { value: 'medium', label: 'Medium', emoji: '🤔' },
                    { value: 'hard', label: 'Hard', emoji: '🧠' }
                  ].map(level => (
                    <button
                      key={level.value}
                      onClick={() => handlePreferenceChange('difficulty', level.value)}
                      disabled={isLoading}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      userProfile?.preferences?.difficulty === level.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-lg mr-2">{level.emoji}</span>
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Voice Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voice Assistant 🎤
                </label>
                <p className="text-xs text-gray-500 mb-3">Choose how questions and words sound when spoken aloud</p>
                
                {availableVoices.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {availableVoices.map((voice, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-800">
                            {voice.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {voice.localService ? 'Local Voice' : 'Online Voice'} • {voice.lang}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => testVoice(voice.name)}
                            disabled={isTestingVoice || isLoading}
                            className="text-blue-500 hover:text-blue-600 text-sm px-2 py-1 rounded transition-colors disabled:opacity-50"
                          >
                            {isTestingVoice ? '🔊' : '▶️'}
                          </button>
                          <button
                            onClick={() => handleVoiceChange(voice.name)}
                            disabled={isLoading}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              currentVoice?.name === voice.name
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {currentVoice?.name === voice.name ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <div className="text-2xl mb-2">🔍</div>
                    <p className="text-sm">Loading voices...</p>
                  </div>
                )}
                
                {/* Voice Quality Controls */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Speaking Rate: {voiceSettings.rate.toFixed(2)}x
                      </label>
                      <span className="text-xs text-gray-500">
                        {voiceSettings.rate < 0.9 ? 'Slower' : voiceSettings.rate > 1.0 ? 'Faster' : 'Normal'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.05"
                      value={voiceSettings.rate}
                      onChange={(e) => handleVoiceSettingChange('rate', e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Slow</span>
                      <span>Fast</span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Pitch: {voiceSettings.pitch.toFixed(2)}x
                      </label>
                      <span className="text-xs text-gray-500">
                        {voiceSettings.pitch < 0.9 ? 'Lower' : voiceSettings.pitch > 1.1 ? 'Higher' : 'Normal'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.05"
                      value={voiceSettings.pitch}
                      onChange={(e) => handleVoiceSettingChange('pitch', e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Volume: {Math.round(voiceSettings.volume * 100)}%
                      </label>
                      <span className="text-xs text-gray-500">
                        {voiceSettings.volume < 0.7 ? 'Quiet' : voiceSettings.volume > 0.95 ? 'Loud' : 'Normal'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={voiceSettings.volume}
                      onChange={(e) => handleVoiceSettingChange('volume', e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Quiet</span>
                      <span>Loud</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      // Apply current settings and test
                      textToSpeech.saveUserPreferences({
                        ...voiceSettings,
                        voiceName: currentVoice?.name || null
                      });
                      textToSpeech.testVoice(
                        currentVoice?.name || null,
                        "This is how your voice settings sound. Adjust the sliders to find your perfect voice!"
                      );
                    }}
                    className="w-full mt-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                  >
                    🔊 Test Voice Settings
                  </button>
                </div>
              </div>

              {/* Sound Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Sound Effects 🔊
                    </label>
                    <p className="text-xs text-gray-500">Hear fun sounds when you learn!</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('sound_enabled', !userProfile?.preferences?.sound_enabled)}
                    disabled={isLoading}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      userProfile?.preferences?.sound_enabled ? 'bg-purple-500' : 'bg-gray-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      userProfile?.preferences?.sound_enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Background Music 🎵
                    </label>
                    <p className="text-xs text-gray-500">Play music while learning</p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('music_enabled', !userProfile?.preferences?.music_enabled)}
                    disabled={isLoading}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      userProfile?.preferences?.music_enabled ? 'bg-purple-500' : 'bg-gray-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      userProfile?.preferences?.music_enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'theme' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Choose Your Theme! 🌈</h3>
              <div className="grid grid-cols-2 gap-4">
                {themes.map(theme => (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeChange(theme.id)}
                    disabled={isLoading}
                    className={`p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                      userProfile?.preferences?.theme === theme.id
                        ? 'border-purple-500'
                        : 'border-gray-200 hover:border-purple-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`${theme.color} ${theme.text} p-4 rounded-lg text-center`}>
                      <div className="text-2xl mb-2">🎨</div>
                      <div className="font-semibold">{theme.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'account' && isLoggedIn && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold mb-4">Account Settings 👤</h3>
              
              {/* Display Name Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name ✏️
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  This is how your name appears in the app
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={displayName || currentDisplayName || ''}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleDisplayNameUpdate}
                    disabled={isLoading || !(displayName || currentDisplayName).trim() || displayName === currentDisplayName}
                    className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
                {userProfile?.email && (
                  <p className="text-xs text-gray-500 mt-2">
                    Email: {userProfile.email}
                  </p>
                )}
              </div>

              {/* Account Info */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-md font-semibold mb-3 text-gray-700">Account Information 📋</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Account Type:</span>
                    <span className="font-medium">Authenticated User</span>
                  </div>
                  {userProfile?.created_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Member Since:</span>
                      <span className="font-medium">
                        {new Date(userProfile.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Reset Progress Section */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-md font-semibold mb-3 text-red-600">Danger Zone ⚠️</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-3">
                    <strong className="text-red-600">Reset All Progress</strong>
                  </p>
                  <p className="text-xs text-gray-600 mb-4">
                    This will delete all your learning progress, scores, achievements, and certificates. 
                    This action cannot be undone. You'll start completely fresh.
                  </p>
                  
                  {!showResetConfirm ? (
                    <button
                      onClick={handleResetProgress}
                      disabled={isResetting}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Reset All Progress
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-red-700">
                        ⚠️ Are you absolutely sure? This cannot be undone!
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleResetProgress}
                          disabled={isResetting}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isResetting ? 'Resetting...' : 'Yes, Reset Everything'}
                        </button>
                        <button
                          onClick={() => setShowResetConfirm(false)}
                          disabled={isResetting}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-semibold disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Changes are saved automatically! ✨
            </div>
            <div className="flex gap-3">
              {isLoggedIn && (
                <button
                  onClick={handleLogout}
                  className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors font-semibold"
                  type="button"
                >
                  🚪 Log Out
                </button>
              )}
              <button
                onClick={onClose}
                className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors"
              >
                Done! 🎉
              </button>
            </div>
          </div>
        </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
  
  // Use React Portal to render modal at document body level
  // This ensures it appears above all other content
  useEffect(() => {
    logger.info('ProfileManager: Component rendered, creating Portal');
    
    // Verify the portal target after a brief delay
    setTimeout(() => {
      const portalElement = document.querySelector('[data-portal="profile-manager"]');
      const fixedElements = document.body.querySelectorAll('.fixed.inset-0');
      
      if (portalElement) {
        logger.debug('ProfileManager: Portal element verified in DOM');
        
        // Check if it's visible
        const styles = window.getComputedStyle(portalElement);
        logger.debug('[ProfileManager] Portal element styles:', {
          display: styles.display,
          visibility: styles.visibility,
          opacity: styles.opacity,
          zIndex: styles.zIndex
        });
      } else if (fixedElements.length > 0) {
        logger.debug('[ProfileManager] Fixed elements found (might be modal):', fixedElements.length);
      } else {
        logger.warn('ProfileManager: Portal element NOT found in DOM');
      }
    }, 200);
  }, []);
  
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  
  if (portalTarget) {
    logger.info('ProfileManager: Creating React Portal to document.body');
    // Add a data attribute to help identify the portal
    const portalContent = (
      <div data-portal="profile-manager" style={{ position: 'relative' }}>
        {modalContent}
      </div>
    );
    return createPortal(portalContent, portalTarget);
  } else {
    logger.warn('ProfileManager: document.body not available, rendering inline');
    return modalContent;
  }
});

ProfileManager.propTypes = {
  onClose: PropTypes.func
};

ProfileManager.displayName = 'ProfileManager';

export default ProfileManager;
